import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { X } from 'lucide-react'
import { useT } from '../lib/i18n.tsx'
import { cn } from './util.ts'

export type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'team' | 'private' | 'register'

export const toneText: Record<Tone, string> = {
  neutral: 'text-ink-muted',
  accent: 'text-accent-text',
  ok: 'text-ok',
  warn: 'text-warn',
  danger: 'text-danger',
  team: 'text-team',
  private: 'text-private',
  register: 'text-register',
}

export const toneSoft: Record<Tone, string> = {
  neutral: 'bg-hover ring-line',
  accent: 'bg-accent-soft ring-accent-line',
  ok: 'bg-ok-soft ring-ok-line',
  warn: 'bg-warn-soft ring-warn-line',
  danger: 'bg-danger-soft ring-danger-line',
  team: 'bg-team-soft ring-team-line',
  private: 'bg-private-soft ring-private-line',
  register: 'bg-register-soft ring-register-line',
}

export const toneLine: Record<Tone, string> = {
  neutral: 'ring-line-strong',
  accent: 'ring-accent-line',
  ok: 'ring-ok-line',
  warn: 'ring-warn-line',
  danger: 'ring-danger-line',
  team: 'ring-team-line',
  private: 'ring-private-line',
  register: 'ring-register-line',
}

export const toneDot: Record<Tone, string> = {
  neutral: 'bg-ink-faint',
  accent: 'bg-accent',
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
  team: 'bg-team',
  private: 'bg-private',
  register: 'bg-register',
}

export interface ChipProps {
  children: ReactNode
  tone?: Tone
  variant?: 'soft' | 'outline' | 'dot'
  size?: 'sm' | 'md'
  icon?: LucideIcon
  mono?: boolean
  dashed?: boolean
  title?: string
  className?: string
  /** Makes the chip removable (filter chips in the omnibar and tables). */
  onRemove?: () => void
  removeLabel?: string
}

/** A small label: <Chip tone="ok">Fresh</Chip>; removable with onRemove. */
export function Chip({
  children,
  tone = 'neutral',
  variant = 'soft',
  size = 'sm',
  icon: Icon,
  mono,
  dashed,
  title,
  className,
  onRemove,
  removeLabel,
}: ChipProps) {
  const t = useT()
  return (
    <span
      title={title}
      className={cn(
        'inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-sm font-medium whitespace-nowrap',
        size === 'sm' ? 'h-5 px-1.5 text-xs' : 'h-6 px-2 text-sm',
        mono && 'font-mono tracking-label uppercase',
        dashed && ['border border-dashed', toneText[tone]],
        !dashed && variant === 'soft' && ['ring-1 ring-inset', toneSoft[tone], toneText[tone]],
        !dashed && variant === 'outline' && ['bg-transparent ring-1 ring-inset', toneLine[tone], toneText[tone]],
        variant === 'dot' && 'bg-hover text-ink-muted ring-1 ring-line ring-inset',
        onRemove && 'pr-0.5',
        className,
      )}
    >
      {variant === 'dot' && <span aria-hidden className={cn('size-1.5 rounded-full', toneDot[tone])} />}
      {Icon && <Icon size={size === 'sm' ? 11 : 13} strokeWidth={2} aria-hidden />}
      <span className="truncate">{children}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel ?? t('common.removeFilter', { label: typeof children === 'string' ? children : '' })}
          className="ml-0.5 inline-flex size-4 items-center justify-center rounded-xs opacity-70 transition hover:bg-press hover:opacity-100"
        >
          <X size={11} strokeWidth={2.25} aria-hidden />
        </button>
      )}
    </span>
  )
}
